<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { APP_NAME } from '@fanfare/shared';

const PILLARS = [
  {
    eyebrow: '01',
    title: 'For fans',
    body: 'Vote, shape tone, protect what matters. Your name in the credits when it ships.',
  },
  {
    eyebrow: '02',
    title: 'For creators',
    body: 'Bring your readers with you. Keep your IP. Get your story to the marquee.',
  },
  {
    eyebrow: '03',
    title: 'For Hollywood',
    body: 'Real fan-demand data before the greenlight. The brief writes itself.',
  },
] as const;

const year = new Date().getFullYear();

// Subtle reveal-on-scroll for the pillars. Respects reduced-motion.
const observed = ref<Set<number>>(new Set());
let io: IntersectionObserver | null = null;

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    [0, 1, 2].forEach((i) => observed.value.add(i));
    return;
  }
  io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const idx = Number((entry.target as HTMLElement).dataset.idx ?? '-1');
          if (idx >= 0) observed.value.add(idx);
        }
      }
    },
    { threshold: 0.3 },
  );
  document.querySelectorAll<HTMLElement>('[data-pillar]').forEach((el) => io?.observe(el));
});

onBeforeUnmount(() => io?.disconnect());
</script>

<template>
  <main class="coming-soon-page flex min-h-screen flex-col">
    <!-- ─── Masthead ─── -->
    <header
      class="coming-soon-masthead grid items-center gap-4 border-b border-[var(--color-hairline)] px-6 py-4 sm:px-12 sm:py-5"
      style="grid-template-columns: auto 1fr auto auto;"
    >
      <div class="coming-soon-brand flex items-baseline gap-3">
        <span
          class="coming-soon-brand-mark text-[var(--color-text-primary)] italic"
          style="font-family: var(--font-display); font-variation-settings: 'opsz' 36, 'SOFT' 100, 'wght' 500; font-size: 22px;"
        >
          ✦ {{ APP_NAME }}
        </span>
      </div>
      <div
        class="coming-soon-mast-rule hidden sm:block"
        style="height: 1px; background: linear-gradient(to right, transparent, var(--color-hairline) 30%, var(--color-hairline) 70%, transparent);"
      ></div>
      <span
        class="coming-soon-mast-meta hidden text-[var(--color-text-tertiary)] sm:inline-block font-mono"
        style="font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase;"
      >
        Vol. <b class="text-[var(--color-text-primary)] font-medium">0</b> &nbsp;/&nbsp;
        Issue <b class="text-[var(--color-text-primary)] font-medium">01</b>
      </span>
      <a
        href="mailto:geoffroy.faugerolas@gmail.com"
        class="coming-soon-mast-cta rounded-full border border-[var(--color-hairline)] text-[var(--color-text-secondary)] transition hover:border-[var(--color-brand-400)] hover:text-[var(--color-text-primary)] font-mono"
        style="padding: 5px 14px 4px; font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase;"
      >
        Get in touch
      </a>
    </header>

    <!-- ─── Cover spread ─── -->
    <section
      class="coming-soon-cover relative flex flex-1 items-center overflow-hidden"
      style="padding: clamp(56px, 10vh, 112px) 0;"
    >
      <!-- Cinematic atmospheric backdrop -->
      <div
        class="coming-soon-cover-bg absolute inset-0"
        aria-hidden="true"
        style="
          background:
            radial-gradient(55% 65% at 20% 35%, color-mix(in oklab, var(--color-brand-700) 55%, transparent) 0%, transparent 60%),
            radial-gradient(45% 60% at 80% 75%, color-mix(in oklab, var(--color-surface-700) 100%, transparent) 0%, transparent 65%);
        "
      ></div>

      <div class="coming-soon-cover-inner relative z-10 mx-auto w-full max-w-6xl px-6 sm:px-12">
        <!-- Top metadata strip -->
        <div
          class="coming-soon-meta-row mb-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[var(--color-text-tertiary)]"
          style="font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase;"
        >
          <span class="inline-flex items-center gap-2">
            <span class="size-1.5 rounded-full bg-[var(--color-brand-500)]" style="box-shadow: 0 0 14px var(--color-brand-500);"></span>
            Coming soon · 2026
          </span>
          <span class="text-[var(--color-brand-400)]">FF–DD–001</span>
          <span>fanfare.hiddenplanet.io</span>
        </div>

        <!-- Wordmark — large italic Fraunces, editorial cover energy -->
        <h1
          class="coming-soon-wordmark text-[var(--color-text-primary)]"
          style="
            font-family: var(--font-display);
            font-variation-settings: 'opsz' 144, 'SOFT' 100, 'wght' 320;
            font-style: italic;
            font-size: clamp(72px, 14vw, 200px);
            line-height: 0.92;
            letter-spacing: -0.02em;
            margin: 0;
            max-width: 14ch;
          "
        >
          Where audiences shape the stories they love.
        </h1>

        <!-- Bottom of cover: lede + signed credits -->
        <div
          class="coming-soon-cover-bottom mt-12 grid items-end gap-8 border-t border-[var(--color-hairline)] pt-8 sm:grid-cols-[1fr_auto]"
        >
          <p
            class="coming-soon-lede text-[var(--color-text-primary)]"
            style="
              font-family: var(--font-display);
              font-style: italic;
              font-variation-settings: 'opsz' 60, 'SOFT' 80, 'wght' 350;
              font-size: clamp(18px, 1.9vw, 24px);
              line-height: 1.36;
              max-width: 40ch;
              margin: 0;
            "
          >
            The first community-powered, AI-driven adaptation studio. Built with the fans who already know the story by heart.
          </p>
          <div
            class="coming-soon-signed text-right font-mono text-[var(--color-text-tertiary)]"
            style="font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase; line-height: 1.85;"
          >
            <div class="text-[var(--color-text-primary)] italic" style="font-family: var(--font-display); font-style: italic; font-variation-settings: 'opsz' 24, 'SOFT' 100, 'wght' 380; font-size: 17px; text-transform: none; letter-spacing: 0;">
              Geoffroy Faugérolas
            </div>
            <div class="text-[var(--color-text-primary)] italic" style="font-family: var(--font-display); font-style: italic; font-variation-settings: 'opsz' 24, 'SOFT' 100, 'wght' 380; font-size: 17px; text-transform: none; letter-spacing: 0;">
              Mitch Lusas
            </div>
            <div class="mt-1">Editor &amp; Art Director</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Pillars ─── -->
    <section class="coming-soon-pillars-section border-t border-[var(--color-hairline)]" style="background: var(--color-surface-1000);">
      <div class="mx-auto w-full max-w-6xl px-6 py-16 sm:px-12 sm:py-20">
        <div
          class="coming-soon-pillars-eyebrow mb-8 inline-flex items-center gap-3 text-[var(--color-text-tertiary)] font-mono"
          style="font-size: 10.5px; letter-spacing: 0.28em; text-transform: uppercase;"
        >
          <span class="inline-block h-px w-7 bg-[var(--color-brand-500)]"></span>
          What we're building
        </div>
        <div class="coming-soon-pillars grid grid-cols-1 gap-px sm:grid-cols-3"
          style="background: var(--color-hairline); border: 1px solid var(--color-hairline);"
        >
          <article
            v-for="(p, i) in PILLARS"
            :key="p.title"
            data-pillar
            :data-idx="i"
            class="coming-soon-pillar-card flex flex-col gap-3 p-7 transition-all duration-700"
            :class="observed.has(i) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'"
            style="background: var(--color-surface-950);"
          >
            <span
              class="coming-soon-pillar-eyebrow font-mono text-[var(--color-text-tertiary)]"
              style="font-size: 10.5px; letter-spacing: 0.24em;"
            >
              {{ p.eyebrow }} ·
            </span>
            <h2
              class="coming-soon-pillar-title italic text-[var(--color-text-primary)]"
              style="font-family: var(--font-display); font-style: italic; font-variation-settings: 'opsz' 36, 'SOFT' 80, 'wght' 420; font-size: 26px; line-height: 1.1; margin: 0;"
            >
              {{ p.title }}
            </h2>
            <p
              class="coming-soon-pillar-body text-[var(--color-text-secondary)]"
              style="font-family: var(--font-display); font-variation-settings: 'opsz' 16, 'SOFT' 40, 'wght' 400; font-size: 16px; line-height: 1.5; margin: 0;"
            >
              {{ p.body }}
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- ─── Footer ─── -->
    <footer
      class="coming-soon-footer flex flex-col items-center gap-2 border-t border-[var(--color-hairline)] px-6 py-8 text-[var(--color-text-tertiary)] sm:flex-row sm:justify-between sm:px-12 font-mono"
      style="font-size: 10.5px; letter-spacing: 0.2em; text-transform: uppercase;"
    >
      <div>© {{ year }} FanFare</div>
      <div class="italic" style="font-family: var(--font-display); font-style: italic; font-variation-settings: 'opsz' 24, 'SOFT' 80, 'wght' 380; font-size: 14px; letter-spacing: 0; text-transform: none;">
        Powered by fans. Accelerated by AI.
      </div>
      <div>fanfare.hiddenplanet.io</div>
    </footer>
  </main>
</template>
